import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { SoundStatus } from '../../post-interface';
import { HeaderSettingsContainer } from '../HeaderSettings';
import { MenuColumn, MenuLabel, MenuRow, Options } from '../Options';
import { SourceDetails } from '../../utils/source-constants';
import * as Form from '../form';

export const AdvancedFeedHeader = ({
    source,
    soundStatus: soundsEnabled,
    selectedTweetTypes,
    soundTweetTypes,
    onSoundStatusChange,
    onTweetTypesUpdate,
}: {
    source: Source;
    soundStatus: SoundStatus;
    selectedTweetTypes?: Set<string>;
    soundTweetTypes?: Set<string>;
    onSoundStatusChange: (soundStatus: SoundStatus) => void;
    onTweetTypesUpdate: (username: string, newSelectedTweetTypes?: Set<string>, newSoundTweetTypes?: Set<string>) => void;
} & SourceDetails) => {
    const { username } = source;

    const channelsWithoutMenu = new Set(['gdhdhdhdhgf', 'BWEnews']);

    const [customSound, setCustomSound] = useState<string | null>(null);
    const [isCustomSelected, setIsCustomSelected] = useState(false);

    useEffect(() => {
        const savedSound = localStorage.getItem(`customSound_${username}`);
        const savedSelection = localStorage.getItem(`soundChoice_${username}`);
        if (savedSound) setCustomSound(savedSound);
        setIsCustomSelected(savedSelection === 'custom');
    }, [username]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                setCustomSound(result);
                localStorage.setItem(`customSound_${username}`, result);
                setIsCustomSelected(true);
                localStorage.setItem(`soundChoice_${username}`, 'custom');

                toast.success('Custom sound uploaded');
            };
            reader.readAsDataURL(file);
        } else {
            alert('Please select an audio file.');
        }
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value === 'default') {
            setIsCustomSelected(false);
            setCustomSound(null);
            localStorage.setItem(`soundChoice_${username}`, 'default');
            localStorage.removeItem(`customSound_${username}`);
        } else if (value === 'custom') {
            if (customSound) {
                setIsCustomSelected(true);
                localStorage.setItem(`soundChoice_${username}`, 'custom');
            } else {
                toast.info('First load the sound, then you can choose "Custom" sound');
            }
        }
    };

    const isCustomDisabled = !customSound;

    return (
        <>
            <HeaderSettingsContainer>
                <button
                    className={
                        'size-[24px] bg-contain ' + (soundsEnabled[source.username] ? 'bg-[image:var(--volume-icon-svg)]' : 'bg-[image:var(--mute-icon-svg)]')
                    }
                    onClick={() => {
                        const newSoundsEnabled = { ...soundsEnabled };
                        newSoundsEnabled[source.username] = !newSoundsEnabled[source.username];
                        onSoundStatusChange(newSoundsEnabled);
                    }}
                />
                <Options>
                    <MenuRow>
                        {!channelsWithoutMenu.has(source.username) && (
                            <MenuColumn title='DISPLAY'>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={selectedTweetTypes?.has('tweet')}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('tweet')) {
                                                newSelectedTweetTypes.delete('tweet');
                                            } else {
                                                newSelectedTweetTypes.add('tweet');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Tweets
                                </MenuLabel>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={selectedTweetTypes?.has('retweet') ?? true}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('retweet')) {
                                                newSelectedTweetTypes.delete('retweet');
                                            } else {
                                                newSelectedTweetTypes.add('retweet');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Retweets
                                </MenuLabel>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={selectedTweetTypes?.has('reply') ?? true}
                                        onChange={() => {
                                            const newSelectedTweetTypes = new Set(selectedTweetTypes);
                                            if (newSelectedTweetTypes.has('reply')) {
                                                newSelectedTweetTypes.delete('reply');
                                            } else {
                                                newSelectedTweetTypes.add('reply');
                                            }
                                            onTweetTypesUpdate(source.username, newSelectedTweetTypes, soundTweetTypes);
                                        }}
                                    />
                                    Replies
                                </MenuLabel>
                            </MenuColumn>
                        )}
                        {!channelsWithoutMenu.has(source.username) && (
                            <MenuColumn title='SOUND'>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={soundTweetTypes?.has('tweet') ?? true}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('tweet')) {
                                                newSoundTweetTypes.delete('tweet');
                                            } else {
                                                newSoundTweetTypes.add('tweet');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Tweets
                                </MenuLabel>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={soundTweetTypes?.has('retweet') ?? true}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('retweet')) {
                                                newSoundTweetTypes.delete('retweet');
                                            } else {
                                                newSoundTweetTypes.add('retweet');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Retweets
                                </MenuLabel>
                                <MenuLabel>
                                    <Form.Checkbox
                                        checked={soundTweetTypes?.has('reply') ?? true}
                                        onChange={() => {
                                            const newSoundTweetTypes = new Set(soundTweetTypes);
                                            if (newSoundTweetTypes.has('reply')) {
                                                newSoundTweetTypes.delete('reply');
                                            } else {
                                                newSoundTweetTypes.add('reply');
                                            }
                                            onTweetTypesUpdate(source.username, selectedTweetTypes, newSoundTweetTypes);
                                        }}
                                    />
                                    Replies
                                </MenuLabel>
                            </MenuColumn>
                        )}
                    </MenuRow>
                    <MenuRow>
                        <MenuColumn centerTitle title='CHOOSE SOUND'>
                            <div className='w-full flex items-center justify-between'>
                                <label className='flex'>
                                    <Form.Radio name={`sound_${username}`} value='default' checked={!isCustomSelected} onChange={handleRadioChange} />
                                    <span className='custom-radio'></span> Default
                                </label>
                                <label
                                    className='flex'
                                    onClick={(e) => {
                                        if (isCustomDisabled || !customSound) {
                                            e.preventDefault();
                                            toast.info('First load the sound, then you can choose "Custom" sound');
                                        }
                                    }}
                                >
                                    <Form.Radio
                                        name={`sound_${username}`}
                                        value='custom'
                                        checked={isCustomSelected}
                                        onChange={handleRadioChange}
                                        disabled={isCustomDisabled || !customSound}
                                    />
                                    <span className='custom-radio'></span> Custom
                                </label>
                                <Form.Input
                                    type='file'
                                    className='relative !size-[20px] !p-0 !bg-transparent'
                                    icon={
                                        <div className='absolute top-0 left-0 pointer-events-none size-[20px] bg bg-[image:var(--upload-svg)] cursor-pointer' />
                                    }
                                    inputClassName='opacity-0'
                                    accept='image/*'
                                    onChange={handleFileChange}
                                />
                            </div>
                        </MenuColumn>
                    </MenuRow>
                </Options>
            </HeaderSettingsContainer>
        </>
    );
};
